import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

type FamousInvestorStatusParams = {
  symbol: string;
  lang: Lang;
};

const StatusDataSchema = z.object({
  investorName: z.string().nullish(),
  units: z.union([z.number(), z.literal("-")]).nullish(),
  unitsPct: z
    .union([z.number(), z.enum(["-", "清倉", "Sold Out", "建倉", "New Buy"])])
    .nullish(),
});

const FamousInvestorStatusSchema = z.object({
  fourQuarters: z.array(z.string()).nullish(),
  data: z.array(z.array(StatusDataSchema)).nullish(),
  updatedAt: z.string().nullish(),
});

export type StatusData = z.infer<typeof StatusDataSchema>;
export type FamousInvestorStatus = z.infer<typeof FamousInvestorStatusSchema>;

export const getFamousInvestorStatus = async ({
  symbol,
  lang,
}: FamousInvestorStatusParams): Promise<FamousInvestorStatus> => {
  const endpoint = `/famous-investor-status/${symbol}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return FamousInvestorStatusSchema.parse(await res.json());
};

export const useFamousInvestorStatus = (symbol: string) => {
  const { lang } = useLang();
  return useQuery(
    ["famous-investor-status", symbol, lang],
    () => getFamousInvestorStatus({ symbol, lang }),
    { enabled: !!symbol }
  );
};
